import React, { useCallback, useEffect, useId, useMemo, useRef, useState, } from 'react';
import { PixelRatio, Platform, StyleSheet, View, } from 'react-native';
import Animated, { Easing, interpolate, interpolateColor, useAnimatedScrollHandler, useAnimatedStyle, useDerivedValue, useSharedValue, runOnJS, withRepeat, withSequence, withTiming, } from 'react-native-reanimated';
import Svg, { Defs, G, Line, Path, Use } from 'react-native-svg';
import useColors from 'hooks/useColors';
import { triggerHapticFeedback } from 'utils/hapticFeedback';
import Indicator from '../Indicator';
import { rem } from '../values';
import { styles as textStyles } from '../Text';
import { buildChartPath, computeAxisLabels, computeLabelIndexes, computePointXs, } from './layout';
import useDragToPanScroll from './useDragToPanScroll';
import { isWeb } from '../../constants';
// ─── Constants ─────────────────────────────────────────────────────────────
// Layout
const MAX_VISIBLE_POINTS = 3;
const CHART_HEIGHT = rem(200);
const CHART_VERTICAL_PADDING = rem(50);
const CHART_X_AXIS_PADDING = rem(40);
const POINTER_OFFSET_RATIO_DEFAULT = 1; // 0.5 = 50% of the chart width
const X_AXIS_HEIGHT = textStyles.Text_12.lineHeight + rem(2);
const X_AXIS_TOP_MARGIN = rem(8);
// Chart line — values match design-system/LineChart for a consistent glow
const LINE_STROKE_WIDTH = 1.66;
const LINE_GLOW_STROKE_WIDTH = Platform.OS === 'android' ? 5 : 3.32;
const LINE_GLOW_OPACITY = Platform.OS === 'android' ? 0.2 : 0.1;
// Active point marker
const ACTIVE_POINT_RADIUS = rem(5);
const ACTIVE_POINT_RING_RADIUS = rem(8);
const ACTIVE_DOT_BORDER_WIDTH = rem(2);
const ACTIVE_DOT_INNER_RADIUS = ACTIVE_POINT_RADIUS - ACTIVE_DOT_BORDER_WIDTH;
// Snap the marker to whole physical pixels — fractional offsets antialias
// asymmetrically, which reads as a misshapen circle on downsampled displays
// (iPhone mini/Plus, where scale !== nativeScale).
const PIXEL_DENSITY = PixelRatio.get();
// Round a dp value to the nearest whole physical pixel. Positions fed to the
// native snap implementation must sit on the pixel grid — Android converts
// them with a truncating cast, so any fractional pixel rests the scroll a
// hair left of the gridline drawn at the same dp value.
const roundToPixelGrid = (value) => Math.round(value * PIXEL_DENSITY) / PIXEL_DENSITY;
const SHRUNK_BASE_SCALE = 0.7;
// Pulse animation
const PULSE_DURATION = 900;
const FULL_PULSE_AMPLITUDE = 0.2;
const SUBTLE_PULSE_AMPLITUDE = 0.2;
// Drag transitions
const DRAG_START_DURATION = 200;
const DRAG_RELEASE_DURATION = 600;
const PULSE_PEAK_TRANSITION_DURATION = 400;
// A light tick per point while scrolling, at most one per this interval, so a
// fling gives a few ticks rather than one per point.
const MIN_HAPTIC_INTERVAL = 60;
// selection is iOS-only in react-native-haptic-feedback; clockTick is
// Android's closest light tick.
const triggerPointHaptic = () => triggerHapticFeedback(Platform.OS === 'ios' ? 'selection' : 'clockTick');
// Snap proximity — start of the ramp where axis label / gridline highlight
// transitions from "neutral" to "active" as the scroll approaches a snap point.
const SNAP_PROXIMITY_THRESHOLD = 0.8;
// When `showLastPointLabel` is on, the trailing span — from the last group
// label to the final data point in that group — is scaled by this factor.
// 1 = original full pointSpacing; <1 = compresses the whole trailing group
// (intermediate orphans still split the shrunken span evenly).
const LAST_POINT_SPACING_FACTOR = 0.75;
const ChartPath = ({ d, width, height, stroke, withGlow, }) => {
    const pathId = useId();
    // Round stroke caps extend half the stroke-width past the endpoints, so the
    // SVG needs horizontal padding to avoid clipping at x=0 and x=width.
    const padX = Math.ceil(LINE_GLOW_STROKE_WIDTH / 2);
    let glowStyle = null;
    if (withGlow) {
        const strokeStr = String(stroke);
        const webGlow = `color-mix(in srgb, ${strokeStr} 50%, transparent)`;
        glowStyle =
            Platform.OS === 'web'
                ? {
                    filter: `drop-shadow(0 0 5px ${webGlow}) drop-shadow(0 0 5px ${webGlow})`,
                }
                : {
                    shadowColor: stroke,
                    shadowOffset: { width: 0, height: 0 },
                    shadowOpacity: 0.5,
                    shadowRadius: 5,
                };
    }
    return (<Svg width={width + padX * 2} height={height} viewBox={`${-padX} 0 ${width + padX * 2} ${height}`} style={[{ marginLeft: -padX }, glowStyle]}>
      <Defs>
        <Path id={`chartLine${pathId}`} d={d} stroke={stroke} strokeLinejoin="round" strokeLinecap="round" fill="transparent"/>
      </Defs>
      <G>
        {withGlow ? (<Use x={0} y={0} href={`#chartLine${pathId}`} opacity={LINE_GLOW_OPACITY} strokeWidth={LINE_GLOW_STROKE_WIDTH}/>) : null}
        <Use x={0} y={0} href={`#chartLine${pathId}`} strokeWidth={LINE_STROKE_WIDTH}/>
      </G>
    </Svg>);
};
const ChartVerticalLine = ({ stroke, strokeWidth = 1, dashed, dashedLarge, rotated, length = CHART_HEIGHT, style, }) => {
    const w = rotated ? length : strokeWidth;
    const h = rotated ? strokeWidth : length;
    const centerTransform = rotated
        ? [{ translateY: -(strokeWidth / 2) }]
        : [{ translateX: -(strokeWidth / 2) }];
    return (<Svg width={w} height={h} style={[{ transform: centerTransform }, style]}>
      <Line x1={rotated ? 0 : strokeWidth / 2} x2={rotated ? length : strokeWidth / 2} y1={rotated ? strokeWidth / 2 : 0} y2={rotated ? strokeWidth / 2 : length} stroke={stroke} 
    // eslint-disable-next-line no-nested-ternary
    strokeDasharray={dashed ? (dashedLarge ? '4 4' : '1 2') : undefined} strokeWidth={strokeWidth}/>
    </Svg>);
};
const AxisLabel = ({ label, index, x, width, snapProgress, withLine, }) => {
    const colors = useColors();
    const primaryColor = colors.text.primary;
    const tertiaryColor = colors.text.tertiary;
    const textColorStyle = useAnimatedStyle(() => {
        if (snapProgress.value.targetIndex !== index) {
            return { color: tertiaryColor };
        }
        return {
            color: interpolateColor(snapProgress.value.progress, [SNAP_PROXIMITY_THRESHOLD, 1], [tertiaryColor, primaryColor]),
        };
    });
    const lineOpacityStyle = useAnimatedStyle(() => {
        if (snapProgress.value.targetIndex !== index) {
            return { opacity: 1 };
        }
        return {
            opacity: interpolate(snapProgress.value.progress, [SNAP_PROXIMITY_THRESHOLD, 1], [1, 0], 'clamp'),
        };
    });
    return (<View style={[styles.axisLabel, { left: x - width / 2, width }]}>
      {withLine ? (<Animated.View style={[styles.axisGridLine, { left: width / 2 }, lineOpacityStyle]}>
          <ChartVerticalLine stroke={colors.borders.divider} strokeWidth={isWeb ? 2 : 1}/>
        </Animated.View>) : null}
      <Animated.Text style={[textStyles.Text_12, textColorStyle]}>
        {label}
      </Animated.Text>
    </View>);
};
const ActivePointMarker = ({ pointerScreenX, pointerY, scrollActivity, pulse, }) => {
    const colors = useColors();
    const dotStyle = useAnimatedStyle(() => ({
        transform: [
            {
                translateX: Math.round((pointerScreenX - ACTIVE_POINT_RADIUS) * PIXEL_DENSITY) /
                    PIXEL_DENSITY,
            },
            {
                translateY: Math.round((pointerY.value - ACTIVE_POINT_RADIUS) * PIXEL_DENSITY) /
                    PIXEL_DENSITY,
            },
        ],
    }));
    const ringStyle = useAnimatedStyle(() => {
        const baseScale = interpolate(scrollActivity.value, [0, 1], [1, SHRUNK_BASE_SCALE], 'clamp');
        const amplitude = interpolate(scrollActivity.value, [0, 1], [FULL_PULSE_AMPLITUDE, SUBTLE_PULSE_AMPLITUDE], 'clamp');
        return {
            transform: [{ scale: baseScale + pulse.value * amplitude }],
            opacity: interpolate(pulse.value, [0, 1], [0.3, 0.1], 'extend'),
        };
    });
    return (<Animated.View pointerEvents="none" style={[styles.activeMarker, dotStyle]}>
      <Animated.View pointerEvents="none" style={[
            styles.activeRing,
            { backgroundColor: colors.elements.brand },
            ringStyle,
        ]}/>
      <View style={[styles.activeDot, { backgroundColor: colors.elements.brand }]}>
        <View style={[
            styles.activeDotInner,
            { backgroundColor: colors.background.light },
        ]}/>
      </View>
    </Animated.View>);
};
const ScrollingLineChart = ({ data, pointerOffsetRatio = POINTER_OFFSET_RATIO_DEFAULT, selectedIndex, Header, isFetching, grouped = false, showLastPointLabel = false, }) => {
    const colors = useColors();
    // ---- Container width ----
    const [width, setWidth] = useState(0);
    const onLayout = useCallback((e) => {
        setWidth(e.nativeEvent.layout.width);
    }, []);
    const scrollViewRef = useRef(null);
    // ---- Derived layout dimensions ----
    // Bucketing is fully opt-in: when `grouped` is false, we skip the
    // layout computation entirely and use the original uniform math
    // (pointXs === null is the marker for "uniform mode").
    const labelIndexes = useMemo(() => (grouped ? computeLabelIndexes(data, true) : null), [grouped, data]);
    // In grouped mode, pointSpacing represents the width of one inter-label
    // group. In uniform mode, it's the width between adjacent data points.
    const visibleBuckets = Math.max(1, Math.min(MAX_VISIBLE_POINTS, grouped && labelIndexes && labelIndexes.length >= 2
        ? labelIndexes.length - 1
        : data.length - 1));
    // Floored to whole physical pixels (not dp), so every multiple of
    // pointSpacing stays on the pixel grid and snap rest positions round to
    // the same pixel as the gridlines.
    const pointSpacing = Math.floor(((width - CHART_X_AXIS_PADDING * 2) / visibleBuckets) * PIXEL_DENSITY) / PIXEL_DENSITY;
    // With a single data point, force the pointer to the centre regardless of
    const effectivePointerRatio = data.length <= 1 ? 0.5 : pointerOffsetRatio;
    // Map the 0–1 ratio so it spans the chart's usable width (inset by the x-axis
    // padding), instead of the full container width.
    const pointerScreenX = roundToPixelGrid(CHART_X_AXIS_PADDING +
        effectivePointerRatio * (width - CHART_X_AXIS_PADDING * 2));
    const chartInnerHeight = CHART_HEIGHT - CHART_VERTICAL_PADDING * 2;
    const pointXs = useMemo(() => grouped && labelIndexes
        ? computePointXs(data, labelIndexes, pointSpacing, showLastPointLabel ? LAST_POINT_SPACING_FACTOR : 1
        // Intra-group points split the span fractionally — put them back
        // on the pixel grid so snapping to them is exact too.
        ).map(roundToPixelGrid)
        : null, [grouped, data, labelIndexes, pointSpacing, showLastPointLabel]);
    // Axis labels — pure render concern, doesn't affect positioning. When
    // `showLastPointLabel` is on, appends a forced entry for the final data
    // point (with `useGroupLabel: false` so it shows the per-point label).
    const axisLabels = useMemo(() => grouped && labelIndexes
        ? computeAxisLabels(labelIndexes, data.length, showLastPointLabel)
        : null, [grouped, labelIndexes, data.length, showLastPointLabel]);
    const dataWidth = pointXs
        ? pointXs[pointXs.length - 1] ?? 0
        : Math.max(0, (data.length - 1) * pointSpacing);
    // Snapping always uses absolute offsets, never snapToInterval: Android
    // truncates the interval to whole pixels and multiplies it by the index,
    // so the sub-pixel loss compounds and points drift off their gridlines
    // as you scroll right. Absolute offsets are converted individually and
    // the error stays under one pixel at every index.
    const snapOffsets = useMemo(() => pointXs ?? data.map((_, i) => roundToPixelGrid(i * pointSpacing)), [pointXs, data, pointSpacing]);
    // ---- Point positions & path ----
    const pointYs = useMemo(() => {
        if (data.length === 0)
            return [];
        const values = data.map((d) => d.value);
        const min = Math.min(...values);
        const max = Math.max(...values);
        return values.map((v) => {
            const normalized = max === min ? 0.5 : (v - min) / (max - min);
            return CHART_VERTICAL_PADDING + (1 - normalized) * chartInnerHeight;
        });
    }, [data, chartInnerHeight]);
    const dPath = useMemo(() => {
        if (pointYs.length === 0)
            return '';
        if (pointXs)
            return buildChartPath(pointYs, pointXs);
        // Uniform mode: x = i * pointSpacing inline, no array allocation.
        return pointYs
            .map((y, i) => `${i === 0 ? 'M' : 'L'} ${i * pointSpacing} ${y}`)
            .join(' ');
    }, [pointYs, pointXs, pointSpacing]);
    // ---- Initial state ----
    const initialIndex = data.length > 0 ? data.length - 1 : 0;
    // ---- Animation state ----
    const snapProgress = useSharedValue({
        targetIndex: initialIndex,
        progress: 1,
    });
    const scrollActivity = useSharedValue(0);
    const pulse = useSharedValue(0);
    const pointerY = useSharedValue(0);
    const lastIndex = useDerivedValue(() => selectedIndex.value);
    // Ignore the mount-time x=0 scroll event so it can't overwrite the
    // parent-owned selectedIndex back to 0 before the header has a chance to
    // render the latest point. Flips true on the first user drag.
    const userScrolled = useSharedValue(false);
    const lastHapticAtRef = useRef(0);
    useEffect(() => {
        // eslint-disable-next-line no-param-reassign
        selectedIndex.value = initialIndex;
    }, [initialIndex, selectedIndex]);
    useEffect(() => {
        if (pointYs.length > 0) {
            pointerY.value = pointYs[pointYs.length - 1];
        }
    }, [pointYs, pointerY]);
    useEffect(() => {
        pulse.value = withRepeat(withTiming(1, {
            duration: PULSE_DURATION,
            easing: Easing.inOut(Easing.ease),
        }), -1, true);
    }, [pulse]);
    // ---- Initialise scroll position to the latest point ----
    // The contentOffset prop alone isn't enough on native: the New Architecture
    // recycles iOS scroll views and only applies contentOffset when it differs
    // from the recycled view's old props, so re-entering the screen could rest
    // the chart on an older point while selectedIndex (header, factors) says
    // the latest. Scroll explicitly, and again whenever the data width changes
    // (cached data replaced by a refetch), until the user takes over.
    useEffect(() => {
        if (width === 0 || dataWidth <= 0 || userScrolled.value)
            return;
        scrollViewRef.current?.scrollTo?.({
            x: dataWidth,
            animated: false,
        });
    }, [width, dataWidth, userScrolled]);
    // ---- Web: drag-to-pan (mouse) ----
    // The hook is a no-op on native; on web it wires pointer events to the
    // scroll node so mouse-drag mimics the touch pan we get for free on mobile.
    const handleDragStart = useCallback(() => {
        userScrolled.value = true;
        scrollActivity.value = withTiming(1, { duration: DRAG_START_DURATION });
        pulse.value = withSequence(withTiming(0, {
            duration: DRAG_START_DURATION,
            easing: Easing.inOut(Easing.ease),
        }), withRepeat(withTiming(1, {
            duration: PULSE_DURATION,
            easing: Easing.inOut(Easing.ease),
        }), -1, true));
    }, [pulse, scrollActivity, userScrolled]);
    const handleDragEnd = useCallback(() => {
        scrollActivity.value = withTiming(0, { duration: DRAG_RELEASE_DURATION });
        pulse.value = withSequence(withTiming(1, {
            duration: PULSE_PEAK_TRANSITION_DURATION,
            easing: Easing.inOut(Easing.ease),
        }), withRepeat(withTiming(0, {
            duration: PULSE_DURATION,
            easing: Easing.inOut(Easing.ease),
        }), -1, true));
    }, [pulse, scrollActivity]);
    useDragToPanScroll({
        scrollViewRef,
        enabled: width > 0 && data.length > 1,
        snapOffsets,
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
    });
    // ---- Index change handler ----
    const onIndexChange = useCallback((index) => {
        // eslint-disable-next-line no-param-reassign
        selectedIndex.value = index;
        const now = Date.now();
        if (now - lastHapticAtRef.current >= MIN_HAPTIC_INTERVAL) {
            lastHapticAtRef.current = now;
            triggerPointHaptic();
        }
    }, [selectedIndex]);
    // ---- Scroll handler ----
    const scrollHandler = useAnimatedScrollHandler({
        onScroll: (event) => {
            if (pointYs.length === 0)
                return;
            const { x } = event.contentOffset;
            let lowIdx;
            let highIdx;
            let t;
            let index;
            if (pointXs) {
                // Bucketed: linear scan over non-uniform x positions.
                lowIdx = 0;
                for (let i = 1; i < pointXs.length; i += 1) {
                    if (pointXs[i] <= x) {
                        lowIdx = i;
                    }
                    else {
                        break;
                    }
                }
                highIdx = Math.min(lowIdx + 1, pointXs.length - 1);
                const segWidth = pointXs[highIdx] - pointXs[lowIdx];
                t =
                    segWidth === 0
                        ? 0
                        : Math.max(0, Math.min(1, (x - pointXs[lowIdx]) / segWidth));
                index = t < 0.5 ? lowIdx : highIdx;
            }
            else {
                // Uniform: single division, no array.
                const raw = x / pointSpacing;
                index = Math.max(0, Math.min(Math.round(raw), data.length - 1));
                const clamped = Math.max(0, Math.min(raw, pointYs.length - 1));
                lowIdx = Math.floor(clamped);
                highIdx = Math.min(lowIdx + 1, pointYs.length - 1);
                t = clamped - lowIdx;
            }
            const distFromSnap = index === lowIdx ? t : 1 - t;
            snapProgress.value = {
                targetIndex: index,
                progress: 1 - distFromSnap * 2,
            };
            pointerY.value = pointYs[lowIdx] * (1 - t) + pointYs[highIdx] * t;
            if (userScrolled.value && index !== lastIndex.value) {
                runOnJS(onIndexChange)(index);
            }
        },
        onBeginDrag: () => {
            userScrolled.value = true;
            // Transition to "subtle" pulse state: smaller ring, drive pulse to trough
            // so the shrink + pulse merge into a single motion.
            scrollActivity.value = withTiming(1, { duration: DRAG_START_DURATION });
            pulse.value = withSequence(withTiming(0, {
                duration: DRAG_START_DURATION,
                easing: Easing.inOut(Easing.ease),
            }), withRepeat(withTiming(1, {
                duration: PULSE_DURATION,
                easing: Easing.inOut(Easing.ease),
            }), -1, true));
        },
        onEndDrag: () => {
            // Transition back to "full" pulse state: drive pulse to peak so the
            // ring re-emerges in a single motion to its maximum scale.
            scrollActivity.value = withTiming(0, { duration: DRAG_RELEASE_DURATION });
            pulse.value = withSequence(withTiming(1, {
                duration: PULSE_PEAK_TRANSITION_DURATION,
                easing: Easing.inOut(Easing.ease),
            }), withRepeat(withTiming(0, {
                duration: PULSE_DURATION,
                easing: Easing.inOut(Easing.ease),
            }), -1, true));
        },
    });
    // ---- Render ----
    const hideHeader = data.length === 0;
    const isLoading = isFetching && data.length === 0;
    return (<View>
      {Header ? (<View pointerEvents={hideHeader ? 'none' : 'auto'} style={hideHeader ? styles.hidden : null}>
          <Header data={data} selectedIndex={selectedIndex} snapProgress={snapProgress}/>
        </View>) : null}
      <View onLayout={onLayout} style={styles.wrapper}>
        {width > 0 ? (<>
            <Animated.ScrollView ref={scrollViewRef} horizontal showsHorizontalScrollIndicator={false} scrollEventThrottle={16} snapToOffsets={snapOffsets} decelerationRate="fast" onScroll={scrollHandler} contentOffset={{ x: dataWidth, y: 0 }} contentContainerStyle={{
                paddingLeft: pointerScreenX,
                paddingRight: width - pointerScreenX,
            }}>
              <View>
                <View style={[styles.chartArea, { width: dataWidth }]}>
                  {data.length > 1 ? (<ChartPath d={dPath} width={dataWidth} height={CHART_HEIGHT} stroke={colors.elements.brand} withGlow/>) : null}
                </View>
                {data.length > 0 ? (<View style={[styles.axisRow, { width: dataWidth }]}>
                    {grouped && axisLabels && pointXs
                    ? axisLabels.map(({ index: i, useGroupLabel }) => {
                        const label = useGroupLabel
                            ? data[i].groupLabel ?? data[i].label
                            : data[i].label;
                        return (<AxisLabel key={`${label}-${i}`} label={label} index={i} x={pointXs[i]} width={pointSpacing} snapProgress={snapProgress} withLine={useGroupLabel}/>);
                    })
                    : data.map((entry, i) => (<AxisLabel key={`${entry.label}-${i}`} label={entry.label} index={i} x={i * pointSpacing} width={pointSpacing} snapProgress={snapProgress} withLine/>))}
                  </View>) : null}
              </View>
            </Animated.ScrollView>

            {data.length === 0 ? null : (<View pointerEvents="none" style={[styles.pointerOverlay, { left: pointerScreenX }]}>
                <ChartVerticalLine stroke={colors.borders.strongDivider} strokeWidth={1} dashed style={StyleSheet.absoluteFill}/>
              </View>)}

            {data.length === 1 ? (<ChartVerticalLine rotated dashedLarge dashed stroke={colors.elements.brandMedium} length={width} strokeWidth={2} style={{
                    position: 'absolute',
                    top: pointYs[0],
                    left: pointerScreenX,
                }}/>) : null}

            {data.length > 0 ? (<ActivePointMarker pointerScreenX={pointerScreenX} pointerY={pointerY} scrollActivity={scrollActivity} pulse={pulse}/>) : null}
          </>) : null}
      </View>
      {isLoading ? (<View pointerEvents="none" style={styles.loadingOverlay}>
          <Indicator dark/>
        </View>) : null}
    </View>);
};
export default ScrollingLineChart;
// ─── Styles ────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
    wrapper: {
        alignSelf: 'stretch',
        position: 'relative',
        height: CHART_HEIGHT + X_AXIS_HEIGHT + X_AXIS_TOP_MARGIN,
    },
    chartArea: {
        height: CHART_HEIGHT,
        zIndex: 1,
    },
    axisRow: {
        position: 'relative',
        marginTop: X_AXIS_TOP_MARGIN,
        height: X_AXIS_HEIGHT,
    },
    axisLabel: {
        position: 'absolute',
        alignItems: 'center',
    },
    axisGridLine: {
        position: 'absolute',
        top: -CHART_HEIGHT - X_AXIS_TOP_MARGIN,
    },
    pointerOverlay: {
        position: 'absolute',
        top: 0,
        width: 1,
        height: CHART_HEIGHT,
    },
    loadingOverlay: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        justifyContent: 'center',
    },
    hidden: {
        opacity: 0,
    },
    activeMarker: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: ACTIVE_POINT_RADIUS * 2,
        height: ACTIVE_POINT_RADIUS * 2,
    },
    // Nested circles instead of borderWidth — iOS lets the background colour
    // bleed past a rounded border on tiny views, which ruins the circle.
    activeDot: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: ACTIVE_POINT_RADIUS * 2,
        height: ACTIVE_POINT_RADIUS * 2,
        borderRadius: ACTIVE_POINT_RADIUS,
        alignItems: 'center',
        justifyContent: 'center',
    },
    activeDotInner: {
        width: ACTIVE_DOT_INNER_RADIUS * 2,
        height: ACTIVE_DOT_INNER_RADIUS * 2,
        borderRadius: ACTIVE_DOT_INNER_RADIUS,
    },
    activeRing: {
        position: 'absolute',
        width: ACTIVE_POINT_RING_RADIUS * 2,
        height: ACTIVE_POINT_RING_RADIUS * 2,
        borderRadius: ACTIVE_POINT_RING_RADIUS,
        top: ACTIVE_POINT_RADIUS - ACTIVE_POINT_RING_RADIUS,
        left: ACTIVE_POINT_RADIUS - ACTIVE_POINT_RING_RADIUS,
    },
});
